import { CheckOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Input, message, Spin, Upload } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { UzPhoneInput } from "@/shared/components/UzPhoneInput";
import { parseApiError } from "@/shared/utils/error";
import { normalizeUzPhoneInput, toUzPhoneE164 } from "@/shared/utils/uz-phone";
import { type OrganizationSavePayload, organizationsApi } from "../api/organizations.api";
import { OrganizationBackLink } from "../components/OrganizationBackLink";

const { TextArea } = Input;

export function OrganizationFormPage() {
	const navigate = useNavigate();
	const { organizationId } = useParams();
	const queryClient = useQueryClient();
	const isEdit = Boolean(organizationId);
	const [name, setName] = useState("");
	const [code, setCode] = useState("");
	const [phone, setPhone] = useState("");
	const [address, setAddress] = useState("");
	const [director, setDirector] = useState("");
	const [stir, setStir] = useState("");
	const [accountNumber, setAccountNumber] = useState("");
	const [bankMfo, setBankMfo] = useState("");
	const [oked, setOked] = useState("");
	const [deliveryYear, setDeliveryYear] = useState("");
	const [adminFirstName, setAdminFirstName] = useState("");
	const [adminLastName, setAdminLastName] = useState("");
	const [adminPhone, setAdminPhone] = useState("");
	const [adminPassword, setAdminPassword] = useState("");

	const { data: organization, isLoading } = useQuery({
		queryKey: ["organization", organizationId],
		queryFn: () => {
			if (!organizationId) {
				throw new Error("Organization ID is required");
			}
			return organizationsApi.getOrganization(organizationId);
		},
		enabled: isEdit,
	});

	useEffect(() => {
		if (organization && isEdit) {
			setName(organization.name || "");
			setCode(organization.code || "");
			setPhone(normalizeUzPhoneInput(organization.phone || ""));
			setAddress(organization.address || "");
			setDirector(organization.directorName || "");
			setStir(organization.inn || "");
			setAccountNumber(organization.accountNumber || "");
			setBankMfo(organization.bankMfo || "");
			setOked(organization.oked || "");
			setDeliveryYear(organization.completionYear || "");
		}
	}, [organization, isEdit]);

	const mutation = useMutation({
		mutationFn: (data: OrganizationSavePayload) =>
			isEdit && organizationId
				? organizationsApi.updateOrganization(organizationId, data)
				: organizationsApi.createOrganization(data),
		onSuccess: (data) => {
			message.success(`Tashkilot muvaffaqiyatli ${isEdit ? "yangilandi" : "yaratildi"}!`);
			queryClient.invalidateQueries({ queryKey: ["organizations"] });
			queryClient.invalidateQueries({ queryKey: ["organization", data.id] });
			navigate(`/organizations/${data.id}`);
		},
		onError: (error) => {
			message.error(parseApiError(error));
		},
	});

	const handleSubmit = () => {
		if (!(name.trim() && code.trim())) {
			message.error("Tashkilot nomi va kodi majburiy!");
			return;
		}

		if (isEdit) {
			const normalizedPhone = phone.trim() ? toUzPhoneE164(phone) : "";
			if (phone.trim() && !normalizedPhone) {
				message.error("Telefon raqamni to'liq kiriting.");
				return;
			}
			mutation.mutate({
				name: name.trim(),
				code: code.trim(),
				phone: normalizedPhone || "",
				address: address.trim(),
				directorName: director.trim(),
				inn: stir.trim(),
				accountNumber: accountNumber.trim(),
				bankMfo: bankMfo.trim(),
				oked: oked.trim(),
				completionYear: deliveryYear.trim(),
			});
		} else {
			if (
				!(
					adminFirstName.trim() &&
					adminLastName.trim() &&
					adminPhone.trim() &&
					adminPassword.trim()
				)
			) {
				message.error("Tashkilot admini ma'lumotlari to'liq kiritilishi shart!");
				return;
			}
			const normalizedOrganizationPhone = phone.trim() ? toUzPhoneE164(phone) : "";
			if (phone.trim() && !normalizedOrganizationPhone) {
				message.error("Tashkilot telefon raqamini to'liq kiriting.");
				return;
			}
			const normalizedAdminPhone = toUzPhoneE164(adminPhone);
			if (!normalizedAdminPhone) {
				message.error("Admin telefon raqamini to'liq kiriting.");
				return;
			}
			mutation.mutate({
				name: name.trim(),
				code: code.trim(),
				phone: normalizedOrganizationPhone || "",
				address: address.trim(),
				directorName: director.trim(),
				inn: stir.trim(),
				accountNumber: accountNumber.trim(),
				bankMfo: bankMfo.trim(),
				oked: oked.trim(),
				completionYear: deliveryYear.trim(),
				admin: {
					firstName: adminFirstName.trim(),
					lastName: adminLastName.trim(),
					phone: normalizedAdminPhone,
					password: adminPassword,
				},
			});
		}
	};

	if (isEdit && isLoading) {
		return (
			<div style={{ padding: 40, textAlign: "center" }}>
				<Spin size="large" />
			</div>
		);
	}

	return (
		<div className="crm-organization-create">
			<div className="crm-page-head">
				<OrganizationBackLink placement="action" />
				<div className="crm-page-head-right">
					<h1>{isEdit ? "Tashkilotni tahrirlash" : "Yangi tashkilot"}</h1>
					<p>
						{isEdit
							? "Tashkilot ma'lumotlarini o'zgartirish"
							: "Tashkilot va uning birinchi administratorini yaratish"}
					</p>
				</div>
			</div>

			<div className="crm-grid crm-form-grid">
				<div className="crm-card">
					<div className="crm-card-head">
						<h3>Tashkilot ma'lumotlari</h3>
					</div>
					<div className="crm-card-body">
						<div className="crm-field">
							<span>Nomi</span>
							<Input
								placeholder="Masalan: Unicorp Group"
								value={name}
								onChange={(e) => setName(e.target.value)}
							/>
						</div>
						<div className="crm-inline-fields">
							<div className="crm-field">
								<span>Kod (URL slug)</span>
								<Input
									placeholder="unicorp"
									value={code}
									onChange={(e) => setCode(e.target.value)}
								/>
							</div>
							<div className="crm-field">
								<span>Telefon</span>
								<UzPhoneInput value={phone} onChange={setPhone} />
							</div>
						</div>
						<div className="crm-field">
							<span>Manzil</span>
							<TextArea
								placeholder="Viloyat, shahar, ko'cha"
								rows={3}
								value={address}
								onChange={(e) => setAddress(e.target.value)}
							/>
						</div>
						<div className="crm-divider" />
						<div className="crm-section-note">Shartnoma generatsiyasi uchun:</div>
						<div className="crm-inline-fields">
							<div className="crm-field">
								<span>Direktor</span>
								<Input
									placeholder="To'furov. Q"
									value={director}
									onChange={(e) => setDirector(e.target.value)}
								/>
							</div>
							<div className="crm-field">
								<span>STIR (INN)</span>
								<Input
									placeholder="301098139"
									value={stir}
									onChange={(e) => setStir(e.target.value)}
								/>
							</div>
						</div>
						<div className="crm-field">
							<span>Hisob raqam</span>
							<Input
								placeholder="20208000900123456789"
								value={accountNumber}
								onChange={(e) => setAccountNumber(e.target.value)}
							/>
						</div>
						<div className="crm-inline-fields">
							<div className="crm-field">
								<span>MFO</span>
								<Input
									placeholder="00014"
									value={bankMfo}
									onChange={(e) => setBankMfo(e.target.value)}
								/>
							</div>
							<div className="crm-field">
								<span>OKED</span>
								<Input placeholder="68100" value={oked} onChange={(e) => setOked(e.target.value)} />
							</div>
						</div>
						<div className="crm-field">
							<span>Topshirish yili</span>
							<Input
								placeholder="2027 yil 4-chorak"
								value={deliveryYear}
								onChange={(e) => setDeliveryYear(e.target.value)}
							/>
						</div>
						<div className="crm-field">
							<span>Logo</span>
							<Upload listType="picture-card" showUploadList={false} beforeUpload={() => false}>
								<div>
									<PlusOutlined />
									<div style={{ marginTop: 8 }}>Yuklash</div>
								</div>
							</Upload>
						</div>
					</div>
				</div>

				{!isEdit && (
					<div className="crm-card">
						<div className="crm-card-head">
							<h3>Birinchi Org Admin</h3>
						</div>
						<div className="crm-card-body">
							<div className="crm-inline-fields">
								<div className="crm-field">
									<span>Ism</span>
									<Input
										placeholder="Alisher"
										value={adminFirstName}
										onChange={(e) => setAdminFirstName(e.target.value)}
									/>
								</div>
								<div className="crm-field">
									<span>Familiya</span>
									<Input
										placeholder="Ergashev"
										value={adminLastName}
										onChange={(e) => setAdminLastName(e.target.value)}
									/>
								</div>
							</div>
							<div className="crm-field">
								<span>Telefon (login)</span>
								<UzPhoneInput value={adminPhone} onChange={setAdminPhone} />
							</div>
							<div className="crm-field">
								<span>Parol</span>
								<Input.Password
									placeholder="********"
									value={adminPassword}
									onChange={(e) => setAdminPassword(e.target.value)}
								/>
							</div>
							<div className="crm-auto-card">
								<strong>
									<CheckOutlined /> Avtomatik yaratiladi
								</strong>
								<p>
									SMS balans (0) - 3 standart shablon - avto-eslatma sozlamasi - erta to'lov skidka
									shkalasi (1/3/5%)
								</p>
							</div>
						</div>
					</div>
				)}
			</div>

			<div className="crm-form-actions">
				<Button onClick={() => navigate(-1)}>Bekor</Button>
				<Button className="crm-primary-btn" onClick={handleSubmit} loading={mutation.isPending}>
					{isEdit ? "Saqlash" : "Yaratish"}
				</Button>
			</div>
		</div>
	);
}
